<template>
  <MainLayout>
    <div class="d-flex justify-content-between align-items-center mb-5">
      <div class="mb-10">
        <h1 class="text-uppercase">
          <span>{{ $t('import_campaign') }}</span>
        </h1>
      </div>
      <div class="d-flex">
        <div class="ms-3">
          <router-link
            class="btn btn-color10"
            :to="{ name: 'campaigns.index' }"
          >
            {{ $t('cancel') }}
        </router-link>
        </div>
      </div>
    </div>

    <div class="col-md-10 mx-md-auto">
      <template v-if="user">
        <!-- PLATFORM NAME AND IMAGE -->
        <div class="col-lg-8 col-md-10 m-auto mb-8 card py-10 rounded-3">
          <div class="mb-10 col-md-10 d-flex justify-content-between align-items-center m-auto">
            <div class="d-table m-auto">
              <h2 class="text-uppercase d-table-cell">
                <span>{{ $t('platform') }}</span>
              </h2>
            </div>
            <div class="text-end w-100 ms-10" style="justify-items: end;">
              <img style="margin-right: 20px;" class="d-block" :src="integrationInfo.image_url" alt="" width="70">
            </div>
          </div>
          <!-- END PLATFORM NAME AND IMAGE -->

          <!-- IS NOT INTEGRATED -->
          <template v-if="!userHasEnabledIntegrations">
            <div class="col-md-10 m-auto">
              <h3 class="text-uppercase d-table-cell font-size-xxRegular pb-8"> {{ $t('this_channel_is_part_of_pixel_intelligence')}} </h3>
              <p> {{ $t('connect_your_account') }} </p>
              <p>{{ $t('no_campaign_will_be_created') }}</p>

              <div class="text-center mt-10">
                <MetaIntegrationButton
                  :text="$t('connect')"
                  :redirectUri="redirectUri"
                  v-if="form.platform === 'META_ADS'"
                />

                <GoogleIntegrationButton
                  :text="$t('connect')"
                  :redirectUri="redirectUri"
                  v-if="form.platform === 'GOOGLE_ADS'"
                />

                <TikTokIntegrationButton
                  :text="$t('connect')"
                  :redirectUri="redirectUri"
                  v-if="form.platform === 'TIKTOK_ADS'"
                />
              </div>
            </div>
          </template> <!-- END IS NOT INTEGRATED -->

          <!-- GOOGLE ADS -->
          <template v-if="form.platform === 'GOOGLE_ADS' && user.company.enabled_integrations.google_ads">
            <!-- IS INTEGRATED -->
            <div class="text-center" v-if="loader && step === 'google_ads_accounts'">
              <Loader
                :size="'big'"
              />
            </div>
            <div v-else>
              <div class="text-center mb-3">
                <h2 class="font-size-xMedium text-uppercase">
                  <span>{{ $t('account_responsible_for_the_campaign') }}</span>
                </h2>
              </div>
              <div class="col-12 col-md-6 mx-auto mb-10 default-form">
                <div class="form-group w-100">
                  <div class="custom-select">
                    <select class="form-control" v-model.trim="google_ads.selected_account">
                      <option value="" disabled selected>{{ $t('choice') }}</option>
                      <option
                        :value="account.customer_id"
                        v-for="(account, index) in google_ads.accounts"
                        :key="index"
                      >
                        {{ account.descriptive_name }} ({{ account.customer_id | VMask('###-###-####') }})
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- CAMPAIGNS -->
              <template v-if="google_ads.selected_account">
                <div class="text-center" v-if="loader && step === 'google_ads_campaigns'">
                  <Loader
                    :size="'big'"
                  />
                </div>
                <template v-else>
                  <div class="text-center mb-3">
                    <h2 class="font-size-medium highlighted-color1">
                      <span>{{ $t('select_the_campaign_to_import') }}</span>
                    </h2>
                  </div>
                  <div class="col-12 col-md-6 mx-auto mb-10 default-form">
                    <div class="form-group w-100">
                      <div class="custom-select">
                        <select class="form-control" v-model.trim="form.campaign_id">
                          <option value="" disabled selected>{{ $t('choice') }}</option>
                          <option
                            :value="campaign.id"
                            v-for="(campaign, index) in selectedGoogleAdsAccount.campaigns"
                            :key="index"
                          >
                            {{ campaign.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="text-center mb-3">
                    <h2 class="font-size-medium highlighted-color1">
                      <span>{{ $t('advertiser_to_link_the_campaign') }}</span>
                    </h2>
                  </div>
                  <div class="col-12 col-md-6 mx-auto mb-10 default-form">
                    <div class="form-group w-100">
                      <div class="custom-select">
                        <select class="form-control" v-model.trim="form.advertiser_id">
                          <option value="" disabled selected>{{ $t('choice') }}</option>
                          <option
                            :value="advertiser.id"
                            v-for="(advertiser, index) in advertisers"
                            :key="index"
                          >
                            {{ advertiser.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="text-center">
                    <Button
                      class="btn-color10"
                      type="button"
                      :disabled="!form.campaign_id || !form.advertiser_id"
                      :loading="importing"
                      @click="submit"
                    >
                      {{ $t('import') }}
                    </Button>
                  </div>
                </template>
              </template> <!-- END CAMPAIGNS -->
            </div> <!-- END IS INTEGRATED -->
          </template> <!-- END GOOGLE ADS -->

          <!-- META -->
          <template v-if="form.platform === 'META_ADS'">
            <!-- IS INTEGRATED -->
            <div class="text-center" v-if="loader && step === 'meta_accounts'">
              <Loader
                :size="'big'"
              />
            </div>
            <template v-else>
              <div class="py-10 rounded-3" v-if="this.user.company.enabled_integrations.meta_ads">
                <div class="text-center col-lg-10 col-md-10 mb-3 m-auto" style="text-align: left !important;">
                  <h2 class="font-size-medium highlighted-color1">
                    <span>{{ $t('account_responsible_for_the_campaign') }}</span>
                  </h2>
                </div>
                <div class="col-lg-10 col-md-10 mx-auto mb-10 default-form">
                  <div class="form-group w-100">
                    <div class="custom-select">
                      <select class="form-control" v-model.trim="meta.selected_account">
                        <option value="" disabled selected>{{ $t('choice') }}</option>
                        <option
                          :value="account.id"
                          v-for="(account, index) in meta.accounts"
                          :key="index"
                        >
                          {{ account.name }} ({{ account.id | VMask('###-###-####') }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- CAMPAIGNS -->
                <template v-if="meta.selected_account">
                  <div class="text-center" v-if="loader && step === 'meta_campaigns'">
                    <Loader
                      :size="'big'"
                    />
                  </div>
                  <div v-else>
                    <div class="text-center col-md-10 col-lg-10 mb-3 m-auto" style="text-align: left !important;">
                      <h2 class="font-size-medium highlighted-color1">
                        <span>{{ $t('select_the_campaign_to_import') }}</span>
                      </h2>
                    </div>
                    <div class="col-lg-10 col-md-10 mx-auto mb-10 default-form">
                      <div class="form-group w-100">
                        <div class="custom-select">
                          <select class="form-control" v-model.trim="form.campaign_id">
                            <option value="" disabled selected>{{ $t('choice') }}</option>
                            <option
                              :value="campaign.id"
                              v-for="(campaign, index) in selectedMetaAccount.campaigns"
                              :key="index"
                            >
                              {{ campaign.name }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="text-center col-lg-10 col-md-10 mb-3 m-auto" style="text-align: left !important;">
                      <h2 class="font-size-medium highlighted-color1">
                        <span>{{ $t('advertiser_to_link_the_campaign') }}</span>
                      </h2>
                    </div>
                    <div class="col-lg-10 col-md-10 mx-auto mb-10 default-form">
                      <div class="form-group w-100">
                        <div class="custom-select">
                          <select class="form-control" v-model.trim="form.advertiser_id">
                            <option value="" disabled selected>{{ $t('choice') }}</option>
                            <option
                              :value="advertiser.id"
                              v-for="(advertiser, index) in advertisers"
                              :key="index"
                            >
                              {{ advertiser.name }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="text-center">
                      <Button
                        class="btn-color10"
                        type="button"
                        :disabled="!form.campaign_id || !form.advertiser_id"
                        :loading="importing"
                        @click="submit"
                      >
                        {{ $t('import') }}
                      </Button>
                    </div>
                  </div>
                </template> <!-- END CAMPAIGNS -->
              </div>
            </template><!-- END IS INTEGRATED -->
          </template> <!-- END META -->

          <!-- TIKTOK ADS -->
          <template v-if="userHasEnabledIntegrations && form.platform === 'TIKTOK_ADS'">
            <div class="text-center px-5">
              <p> {{ $t('connect_your_account') }} </p>
              <p>{{ $t('no_campaign_will_be_created') }}</p>
              <h3 class="text-uppercase font-size-xxRegular"> CONECTADO </h3>
            </div>
          </template> <!-- END TIKTOK ADS -->

        </div>
      </template>
    </div>
  </MainLayout>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import GoogleAdsService from '@/modules/integrations/services/google-ads-service'
import MetaIntegrationService from '@/modules/integrations/services/meta-integration-service'
import NotificationService from '@/modules/notifications/services/notifications-service'
import { mapState } from 'vuex'
import variables from '@/mixins/variables'

import Button from '@/components/common/Button/Button'
import GoogleIntegrationButton from '@/components/google/GoogleIntegrationButton'
import Loader from '@/components/common/Loader/Loader'
import MainLayout from '@/components/layouts/MainLayout'
import MetaIntegrationButton from '@/components/meta/MetaIntegrationButton'
import TikTokIntegrationButton from '@/components/integrations/TikTokIntegrationButton'

export default {
  mixins: [variables],
  components: {
    Button,
    GoogleIntegrationButton,
    Loader,
    MainLayout,
    MetaIntegrationButton,
    TikTokIntegrationButton
  },
  data () {
    return {
      advertisers: [],
      form: {
        advertiser_id: '',
        campaign_id: '',
        platform: ''
      },
      google_ads: {
        accounts: [],
        selected_account: ''
      },
      importing: false,
      loader: false,
      meta: {
        accounts: [],
        selected_account: ''
      },
      step: 'init'
    }
  },
  metaInfo () {
    return {
      title: this.$t('import_campaign')
    }
  },
  mounted () {
    if (this.$route.query.platform) this.form.platform = this.$route.query.platform
    if (this.$route.name === 'campaigns.meta-ads.create') this.form.platform = 'META_ADS'
    if (this.$route.name === 'campaigns.google-ads.create') this.form.platform = 'GOOGLE_ADS'
    if (this.$route.name === 'campaigns.tiktok-ads.create') this.form.platform = 'TIKTOK_ADS'
    // if (this.form.platform === 'GOOGLE_ADS') this.getGoogleAdsAccounts()
    // if (this.form.platform === 'META_ADS') this.getMetaAccounts()
    this.getAdvertisers()
  },
  watch: {
    'form.platform' (newValue, oldValue) {
      if (newValue === 'GOOGLE_ADS' && this.google_ads.accounts.length === 0 && this.user) this.getGoogleAdsAccounts()
      if (newValue === 'META_ADS' && this.meta.accounts.length === 0 && this.user) this.getMetaAccounts()
    },
    'google_ads.selected_account' (newValue, oldValue) {
      this.getGoogleAdsCampaigns(newValue)
    },
    'meta.selected_account' (newValue, oldValue) {
      this.getMetaCampaigns(newValue)
    },
    user (newUser) {
      this.getAdvertisers()
      if (this.form.platform === 'GOOGLE_ADS' && this.google_ads.accounts.length === 0) this.getGoogleAdsAccounts()
      if (this.form.platform === 'META_ADS' && this.meta.accounts.length === 0) this.getMetaAccounts()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     * Get integration info from router query parameter
     */
    integrationInfo () {
      if (this.$route.name === 'campaigns.tiktok-ads.create') {
        return {
          name: 'TIKTOK_ADS',
          description: this.$t('tiktok_ads_description'),
          image_url: '/images/pages/campaign/logos/tiktok.png'
        }
      } else {
        return this.$route.query.integrationInfo
      }
    },
    /**
     * Redirect URI
    */
    redirectUri () {
      return `${window.location.href}`
    },
    /**
     * Selected Google Ads Account
    */
    selectedGoogleAdsAccount () {
      if (!this.google_ads.selected_account) return

      return this.google_ads.accounts.find(item => {
        return item.customer_id === this.google_ads.selected_account
      })
    },
    /**
     * Selected Google Ads Account
     */
    selectedMetaAccount () {
      if (!this.meta.selected_account) return

      return this.meta.accounts.find(item => {
        return item.id === this.meta.selected_account
      })
    },
    /*
     *  Validates if user has enabled integrations
     */
    userHasEnabledIntegrations () {
      if (this.form.platform === 'GOOGLE_ADS') return this.user.company.enabled_integrations.google_ads
      if (this.form.platform === 'AMAZON_ADS') return this.user.company.enabled_integrations.amazon_ads
      if (this.form.platform === 'META_ADS') return this.user.company.enabled_integrations.meta_ads
      if (this.form.platform === 'TIKTOK_ADS') return this.user.company.enabled_integrations.tiktok_ads
      return false
    }
  },
  methods: {
    /**
     * Change step
    */
    changeStep (step) {
      this.step = step
    },
    /**
     * Get advertisers
    */
    async getAdvertisers () {
      if (!this.user) return false

      this.loader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.user.company.id,
          status: 'ACTIVE',
          items_per_page: 100,
          page: 1,
          viewType: 'listing_for_campaign_creation'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get Google Ads Accounts
    */
    async getGoogleAdsAccounts () {
      if (!this.user.company.enabled_integrations.google_ads) return

      this.loader = true
      this.changeStep('google_ads_accounts')
      try {
        const accounts = await GoogleAdsService.getAccounts()
        const processedAccounts = []

        accounts.forEach(item => {
          // Add it to the array
          const account = JSON.parse(JSON.stringify(item))
          account.campaigns = []
          delete account.children
          processedAccounts.push(account)

          // Parse children
          item.children.forEach(child => {
            // Check if already is included
            const index = processedAccounts.findIndex(i => {
              return i.customer_id === child.customer_id
            })
            const acc = JSON.parse(JSON.stringify(child))
            acc.manager_id = item.customer_id
            if (!acc.campaigns) acc.campaigns = []
            delete acc.children
            // Check if is not included (sometimes accounts appear doubled, both in the main accounts and in the MCC subaccounts)
            if (index < 0) processedAccounts.push(acc)
            else processedAccounts.splice(index, 1, acc)
          })
        })

        processedAccounts.sort((a, b) => (a.descriptive_name > b.descriptive_name) ? 1 : ((b.descriptive_name > a.descriptive_name) ? -1 : 0))

        // Parse accounts
        this.google_ads.accounts = processedAccounts
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get Google Ads Campaigns
    */
    async getGoogleAdsCampaigns (customerId) {
      this.changeStep('google_ads_campaigns')
      this.form.campaign_id = ''
      const index = this.google_ads.accounts.findIndex(item => {
        return item.customer_id === customerId
      })
      const account = this.google_ads.accounts[index]
      // If already has campaigns loaded, return
      if (account.is_loaded_campaigns) return

      this.loader = true
      try {
        const campaigns = await GoogleAdsService.getCampaigns({
          customer_id: customerId,
          manager_id: account.manager_id || null
        })

        campaigns.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))

        this.google_ads.accounts[index].campaigns = campaigns
        this.google_ads.accounts[index].is_loaded_campaigns = true
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get Meta Accounts
     * @returns {Promise<void>}
     */
    async getMetaAccounts () {
      if (!this.user.company.enabled_integrations.meta_ads) return

      this.loader = true
      this.changeStep('meta_accounts')
      try {
        const accounts = await MetaIntegrationService.getAccounts()
        // Parse accounts
        this.meta.accounts = accounts
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },

    /**
     * Get Meta Campaigns
     */
    async getMetaCampaigns (accountId) {
      this.changeStep('meta_campaigns')
      this.form.campaign_id = ''
      const index = this.meta.accounts.findIndex(item => {
        return item.id === accountId
      })
      const account = this.meta.accounts[index]
      // If already has campaigns loaded, return
      if (account.is_loaded_campaigns) return

      this.loader = true
      try {
        const campaigns = await MetaIntegrationService.getCampaigns({
          account_id: accountId
        })
        this.meta.accounts[index].campaigns = campaigns

        this.meta.accounts[index].is_loaded_campaigns = true
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Creates Meta integration when authorizing
     */
    async insertIntegrationToken (code) {
      if (!this.user) return false
      try {
        // eslint-disable-next-line new-cap
        return await new MetaIntegrationService.insertIntegrationToken({
          code: code,
          company_id: this.user.company.id,
          advertiser_id: this.user.company.advertiser.id
        })
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
    },
    async submit () {
      this.importing = true
      try {
        const campaign = await CampaignsService.importCampaign({
          advertiserId: this.form.advertiser_id,
          campaignId: this.form.campaign_id,
          customerId: this.form.platform === 'GOOGLE_ADS' ? this.selectedGoogleAdsAccount.customer_id : this.selectedMetaAccount.id,
          managerId: this.form.platform === 'GOOGLE_ADS' ? this.selectedGoogleAdsAccount.manager_id : null,
          platform: this.form.platform
        })
        const notification = {
          notification: {
            type: 'success',
            content: this.$t('campaign_imported_successfully')
          }
        }
        NotificationService.createGlobalSuccessNotification(notification)
        this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.importing = false
    }
  }
}
</script>
